/**
 * Chapas de ACM para compra: quantas chapas inteiras as peças de cada cor consomem.
 * Usado pela ficha técnica (regras de chapa "inteira") e pelo resumo de compra do gerador de pranchas.
 */
export interface PecaAcm { largura: number; comprimento: number; qtd: number; cor: string; /** Posição da peça nas pranchas (A1, CA, P1…), para o plano de corte. */ pos?: string; /** Largura da chapa da porta desta peça (para misturar larguras no mesmo encaixe). */ larguraChapa?: number }

/** Peças que não cabem na chapa (mais largas ou mais compridas que ela). */
export function pecasForaDaChapa(pecas: PecaAcm[], chapaLargura: number, chapaComprimento: number): number {
    return pecas.filter(p => !((p.largura <= chapaLargura && p.comprimento <= chapaComprimento) || (p.comprimento <= chapaLargura && p.largura <= chapaComprimento)))
        .reduce((a, p) => a + p.qtd, 0);
}

export interface ItemChapa {
    pos: string;
    /** Medidas da peça como sai da chapa (já girada, se for o caso). */
    largura: number;
    comprimento: number;
    girada: boolean;
    /** A peça é maior que a chapa: ocupa uma chapa própria e fica avisada. */
    foraDaChapa: boolean;
}

/** Retalho que sobra de uma chapa (retângulo livre). */
export interface Retalho { largura: number; comprimento: number }

/** Menor retalho que ainda vale guardar: lado mínimo e área mínima (suposição, ajustável). */
export const RETALHO_MINIMO = { lado: 100, areaM2: 0.05 };
export const retalhoUtil = (r: Retalho) => Math.min(r.largura, r.comprimento) >= RETALHO_MINIMO.lado && r.largura * r.comprimento / 1e6 >= RETALHO_MINIMO.areaM2;

export interface ChapaPlano {
    numero: number;
    /** Largura desta chapa (as de um mesmo plano podem ter larguras diferentes). */
    largura: number;
    itens: ItemChapa[];
    /** Sobra desta chapa: área que não virou peça e os retalhos retangulares que dá para guardar. */
    sobraM2: number;
    retalhos: Retalho[];
    /** Área das peças nesta chapa e quanto ela é da chapa inteira. */
    usadoM2: number;
    aproveitamentoPct: number;
}

/**
 * Plano de corte das chapas inteiras (encaixe em colunas: a peça corre no comprimento da chapa; peças curtas
 * empilham na mesma coluna). Aproximação boa para as tiras longas das portas. Diz QUAL peça vai em QUAL chapa.
 *
 * `larguraChapa` na peça deixa misturar larguras de chapa (ex.: 1220 e 1500) no mesmo encaixe: cada peça abre chapa
 * na largura da sua porta, mas pode ocupar a sobra de qualquer chapa já aberta, de qualquer largura.
 */
export function planoChapas(pecas: PecaAcm[], chapaLargura: number, chapaComprimento: number): ChapaPlano[] {
    const itens: Array<{ w: number; l: number; abre: number; item: ItemChapa }> = [];
    pecas.forEach(p => {
        const abre = p.larguraChapa ?? chapaLargura;
        let w = p.largura, l = p.comprimento, girada = false;
        // Gira a peça quando só cabe girada (mais larga ou mais comprida que a chapa, mas cabe de lado)
        if ((w > abre || l > chapaComprimento) && w <= chapaComprimento && l <= abre) { [w, l] = [l, w]; girada = true; }
        // Peça maior que a chapa: conta como uma chapa própria (e é avisada à parte)
        const fora = w > abre || l > chapaComprimento;
        const ww = Math.min(w, abre), ll = Math.min(l, chapaComprimento);
        for (let i = 0; i < p.qtd; i++) itens.push({ w: ww, l: ll, abre, item: { pos: p.pos || '', largura: w, comprimento: l, girada, foraDaChapa: fora } });
    });
    // As peças das chapas mais largas vêm primeiro: abrem as chapas largas, e as estreitas ocupam as sobras
    itens.sort((a, b) => b.abre - a.abre || b.w - a.w || b.l - a.l);

    type Coluna = { w: number; livre: number };
    const chapas: Array<{ largura: number; larguraLivre: number; colunas: Coluna[]; itens: ItemChapa[] }> = [];
    itens.forEach(it => {
        for (const ch of chapas) {
            const col = ch.colunas.find(c => c.w >= it.w && c.livre >= it.l);
            if (col) { col.livre -= it.l; ch.itens.push(it.item); return; }
        }
        let ch = chapas.find(c => c.larguraLivre >= it.w);
        if (!ch) { ch = { largura: it.abre, larguraLivre: it.abre, colunas: [], itens: [] }; chapas.push(ch); }
        ch.larguraLivre -= it.w;
        ch.colunas.push({ w: it.w, livre: chapaComprimento - it.l });
        ch.itens.push(it.item);
    });
    return chapas.map((c, i) => {
        const areaChapa = c.largura * chapaComprimento / 1e6;
        const usadoM2 = c.itens.reduce((a, x) => a + Math.min(x.largura, c.largura) * Math.min(x.comprimento, chapaComprimento) / 1e6, 0);
        // Retalhos: o fim livre de cada coluna (largura da coluna × comprimento livre) e a faixa que sobra na largura
        const retalhos: Retalho[] = [
            ...c.colunas.map(col => ({ largura: col.w, comprimento: col.livre })),
            { largura: c.larguraLivre, comprimento: chapaComprimento },
        ].filter(retalhoUtil);
        return { numero: i + 1, largura: c.largura, itens: c.itens, usadoM2, sobraM2: Math.max(0, areaChapa - usadoM2), retalhos, aproveitamentoPct: Math.min(100, usadoM2 / areaChapa * 100) };
    });
}

/** Quantas chapas inteiras as peças consomem. */
export function chapasNecessarias(pecas: PecaAcm[], chapaLargura: number, chapaComprimento: number): number {
    return planoChapas(pecas, chapaLargura, chapaComprimento).length;
}
